import { difference } from "lodash-es";
import { PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, SubmissionError, type Validator, reduxForm } from "redux-form";

import { CELL_TYPE, COS_PARENT_ID, MAX_LENGTH } from "@/settings/settings.ts";
import { SelForm } from "@/ui-modules/core/form/layout/Form.js";
import { FormWarning } from "@/ui-modules/core/form/layout/FormWarning.js";
import { getFormError, getFormWarning } from "../../app-utils/form.js";
import { trimOrThrow } from "../../app-utils/string.js";
import type {
  Attribute,
  InputParentTypeInput,
  InputTypeAttributeInput,
  ParentType,
  SampleType,
} from "../../graphql.js";
import {
  loadAttributes,
  loadAttributesForDecoration,
  modifyParentType,
  saveNewParentType,
  validateParentTypeExists,
} from "../../redux/modules/sample-admin.js";
import type { IAppState } from "../../redux/reducer.js";
import CheckboxField, { CheckboxReduxFormField } from "../core/redux-form/CheckboxField.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import { FieldTitle } from "../playbooks/FieldTitle.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../playbooks/PlaybookWizardStep.js";
import type { IAttributeWithDefault } from "./AddSampleTypeDialog.js";
import AttachAttributesManager from "./AttachAttributesManager.js";

export interface IAddParentTypeScreenOwnProps extends IPlaybookWizardStepProvidedProps {
  parentType?: ParentType; // for editing context
}

interface IAddParentTypeScreenAppStateProps {
  parentTypes: ParentType[];
  globalFormError: string;
  globalFormWarning: string;
  initialValues: Partial<FormData>; // from InjectedFormProps
}

interface IAddParentTypeScreenDispatchProps {
  saveNewParentType: (data: InputParentTypeInput) => Promise<ParentType>;
  modifyParentType: (data: InputParentTypeInput) => Promise<ParentType>;
  loadAttributes: () => Promise<Attribute[]>;
  validateTypeExists: (name: string) => Promise<boolean>;
  loadAttributeForDecoration: () => Promise<Attribute[]>;
}

export interface IAddParentTypeScreenProps
  extends IAddParentTypeScreenAppStateProps,
    IAddParentTypeScreenDispatchProps,
    IAddParentTypeScreenOwnProps,
    InjectedFormProps<FormData, any>,
    IPlaybookWizardStepInjectedProps {}

function mapStateToProps(state: IAppState, ownProps: IAddParentTypeScreenOwnProps): IAddParentTypeScreenAppStateProps {
  const initValues = ownProps.parentType
    ? {
        [AddParentTypeScreenImpl.FIELD_NAME]: ownProps.parentType.name,
        [AddParentTypeScreenImpl.FIELD_DESCRIPTION]: ownProps.parentType.description,
        [AddParentTypeScreenImpl.FIELD_IS_LISTED]: ownProps.parentType.isListed,
      }
    : {
        [AddParentTypeScreenImpl.FIELD_IS_LISTED]: true,
      };

  return {
    parentTypes: state.sampleAdmin.parentTypes,
    globalFormError: getFormError(AddParentTypeScreenImpl.FORM_NAME, state),
    globalFormWarning: getFormWarning(AddParentTypeScreenImpl.FORM_NAME, state),
    initialValues: initValues,
  };
}

function mapDispatchToProps(dispatch): IAddParentTypeScreenDispatchProps {
  return {
    saveNewParentType: (data: InputParentTypeInput) => dispatch(saveNewParentType(data)),
    modifyParentType: (data: InputParentTypeInput) => dispatch(modifyParentType(data)),
    loadAttributes: () => dispatch(loadAttributes()),
    validateTypeExists: (name: string) => dispatch(validateParentTypeExists(name)),
    loadAttributeForDecoration: () => dispatch(loadAttributesForDecoration()),
  };
}

interface IAddParentTypeDialogState {
  assignedAttributes?: Array<Partial<Attribute>>;
  availableAttributes?: Array<Partial<Attribute>>;
  isLoadingAvailableAttributes?: boolean;
  autoOrMandatoryAttributesAdded?: boolean;
  attributesForDecoration: Attribute[];
}

class AddParentTypeScreenImpl extends PureComponent<IAddParentTypeScreenProps, IAddParentTypeDialogState> {
  public static FORM_NAME = "ADD_PARENT_TYPE_SCREEN";

  public static FIELD_NAME = "NAME";
  public static FIELD_DESCRIPTION = "DESCRIPTION";
  public static FIELD_IS_LISTED = "IS_LISTED";

  // for decoding the column identifiers have a look at AttributeTableColumnAdapterMap
  public static columnDefs: any[] = [
    { name: "Name", columnId: 1401, cellType: CELL_TYPE.STRING, field: "attributeName", fixed: true },
    { name: "Group Name", columnId: 1402, cellType: CELL_TYPE.STRING, field: "groupName" },
    { name: "Attribute Type", columnId: 1404, cellType: CELL_TYPE.STRING, field: "attributeType.name" },
    // column set is always Parent, Sample Types must be empty, and Parent Type cannot be assigned, yet.
    // {name: "Column Set", columnId: 1403, cellType: CELL_TYPE.STRING, field: "columnSet.name"},
    // {name: "Sample Types", columnId: 1405, cellType: CELL_TYPE.STRING_LIST, field: "sampleTypes.name"},
    // {name: "Parent Type", columnId: 1406, cellType: CELL_TYPE.STRING, field: "parentType.name"},
    {
      name: "Vocabulary Terms",
      columnId: 1407,
      cellType: CELL_TYPE.STRING_LIST,
      field: "vocabularyTerms.name",
    },

    { name: "Mandatory", columnId: 1420, cellType: CELL_TYPE.BOOLEAN, field: "mandatory" },
    { name: "Modifiable", columnId: 1422, cellType: CELL_TYPE.BOOLEAN, field: "modifiable" },
    { name: "Automatic", columnId: 1421, cellType: CELL_TYPE.BOOLEAN, field: "automatic" },
    { name: "Disabled", columnId: 1419, cellType: CELL_TYPE.BOOLEAN, field: "disabled" },
    { name: "Edit Once", columnId: 1423, cellType: CELL_TYPE.BOOLEAN, field: "editOnce" },
    { name: "Frozen", columnId: 1424, cellType: CELL_TYPE.BOOLEAN, field: "frozen" },
    { name: "Accept Multiple Values", columnId: 1426, cellType: CELL_TYPE.BOOLEAN, field: "multipleValues" },
    { name: "Is Multiple Values Head", columnId: 1427, cellType: CELL_TYPE.BOOLEAN, field: "isHead" },

    { name: "Max Length", columnId: 1411, cellType: CELL_TYPE.NUMBER, field: "maxLength" },
    { name: "Max Value", columnId: 1410, cellType: CELL_TYPE.NUMBER, field: "maxValue" },
    { name: "Min Value", columnId: 1409, cellType: CELL_TYPE.NUMBER, field: "minValue" },
    { name: "Decimals", columnId: 1412, cellType: CELL_TYPE.NUMBER, field: "decimals" },

    { name: "Formula", columnId: 1415, cellType: CELL_TYPE.STRING, field: "formulaText" },
    { name: "Pattern", columnId: 1413, cellType: CELL_TYPE.STRING, field: "pattern" },
    { name: "Pattern Hint", columnId: 1414, cellType: CELL_TYPE.STRING, field: "patternHint" },
    { name: "Represented Column", columnId: 1418, cellType: CELL_TYPE.STRING, field: "representedColumn" },
    { name: "SQL Statement", columnId: 1416, cellType: CELL_TYPE.STRING, field: "sqlStatement" },
    { name: "Coloring Rule", columnId: 1429, cellType: CELL_TYPE.STRING, field: "coloringRule" },
    { name: "Id Accessor", columnId: 1417, cellType: CELL_TYPE.STRING, field: "idAccessor" },
    { name: "Id", cellType: CELL_TYPE.NUMBER, field: "id" },
  ];

  constructor(props: IAddParentTypeScreenProps) {
    super(props);

    this.state = {
      assignedAttributes: props.parentType?.parentAttributes || [],
      availableAttributes: [],
      isLoadingAvailableAttributes: false,
      autoOrMandatoryAttributesAdded: false,
      attributesForDecoration: [],
    };
  }

  initAvailableAttributes = (attributes: IAttributeWithDefault[]) => {
    // parent attributes must not be shared across parents! Only unassigned attributes can be used for the new parent.
    const availableAttributes = attributes
      .filter((attribute: Attribute) => attribute.columnSet.id === COS_PARENT_ID)
      .filter((attribute: Attribute) => !attribute.parentType && !attribute.representedColumn);
    this.setState({ availableAttributes: availableAttributes });
    return availableAttributes;
  };

  initPreselectedAttributes = (_availableAttributes: IAttributeWithDefault[]) => {
    // only creation case, not editing case
    if (!this.props.parentType) {
      // create the virtual columns whit id < 0 (representer + other required columns)
      const nameColumn: Partial<Attribute> = {
        id: -1,
        attributeName: "Name",
        groupName: "<parent type>",
        representedColumn: "name",
        attributeType: { id: 1, name: "Free Text" },
        mandatory: true,
        automatic: false,
        modifiable: true,
        editable: true,
        valueUniqueConstraint: true,
        decimals: null,
        disabled: true,
        minValue: null,
        maxValue: null,
        maxLength: null,
        pattern: null,
        patternHint: null,
        formulaText: null,
        frozen: true,
        editOnce: true,
        vocabularyTerms: [],
        sqlStatement: null,
        idAccessor: null,
        multipleValues: false,
        isHead: false,
        coloringRule: null,
        columnSet: { id: 3, name: "Parent Data" },
      };
      const statusColumn: Partial<Attribute> = {
        id: -2,
        attributeName: "Status",
        groupName: "<parent type>",
        representedColumn: "status",
        attributeType: { id: 1, name: "Free Text" },
        mandatory: true,
        automatic: true,
        modifiable: true,
        editable: true,
        valueUniqueConstraint: true,
        decimals: null,
        disabled: true,
        minValue: null,
        maxValue: null,
        maxLength: null,
        pattern: null,
        patternHint: null,
        formulaText: null,
        frozen: true,
        editOnce: true,
        vocabularyTerms: [],
        sqlStatement: null,
        idAccessor: null,
        columnSet: { id: 3, name: "Parent Data" },
        multipleValues: false,
        isHead: false,
        coloringRule: null,
      };

      this.setAssignedAttributes([nameColumn, statusColumn]);
    }
  };

  loadAttributes = () => {
    return this.props
      .loadAttributes()
      .catch((error) => {
        throw new SubmissionError({ _error: "Could not load the list of attributes: " + error.message });
      })
      .then(this.initAvailableAttributes);
  };

  componentDidMount() {
    this.setState({ isLoadingAvailableAttributes: true });
    this.loadAttributes()
      .then(this.initPreselectedAttributes)
      .then(() => this.setState({ isLoadingAvailableAttributes: false }));

    this.props.loadAttributeForDecoration().then((attributes) => {
      this.setState({ attributesForDecoration: attributes });
    });
  }

  setAssignedAttributes = (selectedAttributes: Array<Partial<Attribute>>) => {
    // check if one of the new attributes is automatic, and warn admins
    // if a new one is added to an existing parent type with associated sample types.
    if (this.props.parentType?.sampleTypeCount > 0) {
      const addedAttributes: Array<Partial<Attribute>> = difference(selectedAttributes, this.state.assignedAttributes);
      if (addedAttributes.filter((a: Attribute) => a.automatic || a.mandatory).length > 0) {
        this.setState({ autoOrMandatoryAttributesAdded: true });
      }
    }

    this.setState({
      assignedAttributes: selectedAttributes,
    });
  };

  handleSubmitData = (values): Promise<SampleType | ParentType> => {
    // Setable ParentType properties:
    // * defined here
    //    name, description, allowManualStatusChange?
    //    attributes -- without the representers for name and status or the sampleCount field
    //    sortOrder     start counting at 4 to makes space for the representers starting at 1
    // * set on the middle tier
    //    creator, created, id
    // * all derived state variables like
    //    counts, ...
    let attributes: InputTypeAttributeInput[];
    if (this.props.parentType) {
      // editing case
      attributes = this.state.assignedAttributes.map(
        (a: Attribute, index): InputTypeAttributeInput => ({
          attributeId: a.id,
          sortOrder: index + 1,
          // attributeDefault: {} // eventually provide defaults or a template
        }),
      );
    } else {
      const requiredAttCount = 3; // name, status + sampleCount
      attributes = this.state.assignedAttributes
        .filter((a: Attribute) => !a.representedColumn)
        .filter((a: Attribute) => a.id > 0) // attributes with id <= 0 are virtual attributes
        .map(
          (a: Attribute, index): InputTypeAttributeInput => ({
            attributeId: a.id,
            sortOrder: index + 1 + requiredAttCount,
            // attributeDefault: {} // eventually provide defaults or a template
          }),
        );
    }
    const savedData: InputParentTypeInput = {
      id: this.props.parentType?.id,
      name: trimOrThrow(
        values[AddParentTypeScreenImpl.FIELD_NAME],
        new SubmissionError({ _error: "Name value cannot be empty and must contain non white space characters" }),
      ),
      description: trimOrThrow(values[AddParentTypeScreenImpl.FIELD_DESCRIPTION], null),
      isListed: values[AddParentTypeScreenImpl.FIELD_IS_LISTED],
      attributes: attributes,
    };
    const submitData = this.props.parentType ? this.props.modifyParentType : this.props.saveNewParentType;
    return submitData(savedData).then(
      (payload: ParentType) => {
        this.props.closeWizard();
        return payload;
      },
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  // rules to remove a parent attribute
  getRemoveAttributesDisabledTooltip = (selectedAttributeIds: number[]) => {
    // editing case
    if (this.props.parentType && this.props.parentType.sampleCount > 0) {
      return "A parent type attribute can only be removed if there are no samples associated with the parent type";
    } else {
      const selectedAttributes: Array<Partial<Attribute>> = selectedAttributeIds.map((selectedId: number) =>
        this.state.assignedAttributes.find((assignedAttribute: Attribute) => assignedAttribute.id === selectedId),
      );

      if (selectedAttributes.find((attribute: Attribute) => !!attribute.representedColumn)) {
        return "A parent type attribute cannot be removed if it is a representer attribute";
      } else if (
        selectedAttributes.find((attribute: Attribute) => attribute.referencedBy && attribute.referencedBy.length > 0)
      ) {
        return "A parent type attribute cannot be removed if it is dependent on the formula of another existing attribute";
      }
    }
  };

  render(): ReactElement {
    return (
      <PlaybookWizardStep
        {...this.props}
        playbookPage={this.props.playbookPage}
        wizardFooterData={{
          error: this.props.globalFormError || this.props.error,
          warning: this.props.globalFormWarning,
          nextButtonProps: {
            label: this.props.parentType ? "Apply" : "Save",
            isLoading: this.props.submitting || this.state.isLoadingAvailableAttributes,
            onPress: this.props.handleSubmit(this.handleSubmitData),
          },
        }}
      >
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "20px" }}>
            <div id="right-dialog-grid-col" style={{ width: "60%" }}>
              <SelForm>
                <InputReduxFormField
                  componentClass="input"
                  name={AddParentTypeScreenImpl.FIELD_NAME}
                  label={<FieldTitle label={"Name"} mandatory />}
                  placeholder={"Parent type name"}
                  maxLength={MAX_LENGTH.ATTRIBUTE.NAME}
                  component={InputField}
                  validate={AddParentTypeScreenImpl.localNameValidator}
                />
                <CheckboxReduxFormField
                  name={AddParentTypeScreenImpl.FIELD_IS_LISTED}
                  label="Listed in Saved Items"
                  component={CheckboxField}
                />
                {this.state.autoOrMandatoryAttributesAdded && (
                  <FormWarning>
                    {`Automatic or mandatory attributes were added to this parent type.
              Please do not forget to configure the required defaults for related sample types
              (${this.props.parentType?.sampleTypes.map((s: SampleType) => `"${s.name}"`).join(", ")})
              after applying your changes.`}
                  </FormWarning>
                )}
              </SelForm>
            </div>
            <div id="left-dialog-grid-col" style={{ width: "40%" }}>
              <SelForm>
                <InputReduxFormField
                  componentClass="textarea"
                  name={AddParentTypeScreenImpl.FIELD_DESCRIPTION}
                  label="Description"
                  placeholder={"Add a description"}
                  maxLength={MAX_LENGTH.ATTRIBUTE.DESCRIPTION}
                  component={InputField}
                  rows={8}
                />
              </SelForm>
            </div>
          </div>

          <AttachAttributesManager
            columnDefs={AddParentTypeScreenImpl.columnDefs}
            assignedAttributes={this.state.assignedAttributes as Attribute[]}
            availableAttributes={this.state.availableAttributes as Attribute[]}
            handleAttributesChanged={this.setAssignedAttributes}
            refreshAvailableAttributes={this.loadAttributes}
            getRemoveAttributesDisabledTooltip={this.getRemoveAttributesDisabledTooltip}
            isLoadingAvailableAttributes={this.state.isLoadingAvailableAttributes}
            parentContext={true}
            attributesForDecoration={this.state.attributesForDecoration}
          />
        </div>
      </PlaybookWizardStep>
    );
  }

  static localNameValidator: Validator = (value: any, _allValues?: any, props?: IAddParentTypeScreenProps) => {
    const name: string = !value ? "" : value.trim();

    // editing case
    if (props.parentType && name === props.parentType.name) {
      return;
    }
    if (name !== "") {
      // TBD: check for collision with initial name
      //  if props.<parentType> &&  name === props.<parentType>.name --> error
      // validate locally versus already loaded parent types
      if (props.parentTypes.find((spy) => spy.name === name)) {
        return "This parent type name is already in use";
      }
    } else {
      return "The parent type name is required";
    }
    // if any of the local checks failed short-cut and report that first.
    return undefined;
  };

  static async asyncValidate(values, _dispatch, props: IAddParentTypeScreenProps) {
    const CLASS = AddParentTypeScreenImpl;

    if (!values) {
      return;
    }

    // cleanup name
    const name = values[CLASS.FIELD_NAME] || "";

    // editing case
    if (props.parentType && props.parentType.name === name) {
      return;
    }

    if (await props.validateTypeExists(name)) {
      throw {
        [CLASS.FIELD_NAME]: "Parent type name is already in use in the database",
      };
    }
  }
}

const AddParentTypeScreen = connect<
  IAddParentTypeScreenAppStateProps,
  IAddParentTypeScreenDispatchProps,
  IAddParentTypeScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm<{}, any>({
    form: AddParentTypeScreenImpl.FORM_NAME,
    asyncValidate: AddParentTypeScreenImpl.asyncValidate,
    asyncBlurFields: [AddParentTypeScreenImpl.FIELD_NAME], // fields that trigger validation onBlur
  })(AddParentTypeScreenImpl) as any,
);

export { AddParentTypeScreenImpl };
export default AddParentTypeScreen;
